import { FC, useEffect, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { deepCopy, isArray, isNotEmpty } from '@lhb/func';
import { ControlType, getItemDefaultValue } from '../../config';
import styles from './index.module.less';
import { Button, Col, Form, Row } from 'antd';
import FormSetName from '@/common/components/Form/FormSetName';
import Radio from '../Radio';
import Checkbox from '../Checkbox';
import Input from '../Input';
import TextareaInput from '../TextareaInput';
import NumberInput from '../NumberInput';
import Time from '../Time';
import V2Title from '@/common/components/Feedback/V2Title';
import DropdownSelect from '../DropdownSelect';
import TreeSelect from '../TreeSelect';

/**
 * 动态表单项-子表单
 * @description 增减子项参考 src/common/components/business/DynamicComponent/components/BenchBrands.tsx
 * @demo
 */
const Component:FC<{
  form: any;
  formItemData: any;
  disabled: boolean;
  required: boolean;
  tiledFormDataRef: any;
  /** 值变更回调，用于实时更新字段到 tiledFormDataRef.current 的 textValue */
  valueChange: any,
}> = ({
  form,
  formItemData,
  disabled,
  // required,
  tiledFormDataRef,
  valueChange,
}) => {

  // console.log('allComs = tiledFormDataRef.current', tiledFormDataRef.current);
  /** 通过 updateSign 触发页面渲染更新 */
  const [updateSign, setUpdateSign] = useState({});
  updateSign;

  /* state */
  const [groupCount, setGroupCount] = useState(1);// 组个数
  const identification = formItemData.identification; // 字段
  const restriction = isNotEmpty(formItemData?.restriction) ? JSON.parse(formItemData?.restriction || '{}') : {};

  const groupName = formItemData.anotherName || formItemData.name || '分组';

  const value = Form.useWatch(identification);
  const MIN_DATA_COUNT = restriction?.minRows || 1; // 最小数据条数
  const MAX_DATA_COUNT = restriction?.maxRows || 1; // 最大数据条数

  /** 仅展示以下类型的控件 */
  const showControlTypes = [
    ControlType.SINGLE_RADIO.value,
    ControlType.CHECK_BOX.value,
    ControlType.INPUT.value,
    ControlType.TEXT_AREA.value,
    ControlType.INPUT_NUMBER.value,
    ControlType.TIME.value,
    ControlType.TREE_SELECT.value,
  ];

  /* hooks */
  useEffect(() => {
    // 会有默认值，所以下面不需要初始化设置
    // if (!Array.isArray(value) || !value.length) {
    //   methods.updateValue([{}]);
    // }
    setGroupCount(value?.length || 1);
    valueChange?.(identification, value);
  }, [value]);

  /* methods */
  const methods = useMethods({

    getOriData() {
      const obj = {};
      const arr = Array.isArray(formItemData.formConfigList) ? formItemData.formConfigList : [];
      arr.forEach((item) => {
        // console.log('getItemDefaultValue(item)', getItemDefaultValue(item));
        obj[item.identification] = getItemDefaultValue(deepCopy(item));
      });
      return obj;
    },

    /** 新增 */
    add() {
      if (disabled || (isArray(value) && value.length >= MAX_DATA_COUNT)) {
        return;
      }
      methods.updateValue((value || [{}]).concat(methods.getOriData()));
      // setGroupCount(val => val + 1);
    },

    /** 删除 */
    del(curIndex) {
      if (disabled || (isArray(value) && value.length <= MIN_DATA_COUNT)) {
        return;
      }
      methods.updateValue(value.filter((item, index) => index !== curIndex));
      // setTimeout(() => setGroupCount(val => val - 1)); // 很神奇，如果不加这句，就不会触发表单更新
    },

    /** 新增删除时更新值 */
    updateValue(value) {
      form.setFieldValue(identification, deepCopy(value));
      valueChange?.(identification, value);
      /** 以上代码不会触发渲染，通过 updateSign 触发页面渲染更新  */
      setUpdateSign({});
    },

  });

  return (<div className={styles.subForm}>
    {/* 需要进行值录入。如果配置表单对同一个字段配置多次，会导致控制台报警（但不用管） */}
    <FormSetName name={identification} initialValue={[]}></FormSetName>

    <div className={styles.group}>
      {Array.from({ length: groupCount })?.map((item, index) => <div key={index} className={styles.groupItem}>
        {index > 0 && <div className={styles.divider}></div>}
        <V2Title
          divider
          type='H2'
          text={`${groupName} ${index > 0 ? index + 1 : ''}`}
          className={'mb-16'}
          extra={!disabled && <div className={styles.btns}>
            <Button type='link' disabled={disabled || groupCount >= MAX_DATA_COUNT} onClick={methods.add}>新增</Button>
            <Button type='link' disabled={disabled || groupCount <= MIN_DATA_COUNT} onClick={() => methods.del(index)}>删除</Button>
          </div>}
        />
        <Row gutter={24}>
          {Array.isArray(formItemData?.formConfigList) && formItemData.formConfigList.filter(item => showControlTypes.includes(item.controlType)).map((formConfigItem, formConfigIndex) => (
            <Col span={12} key={formConfigIndex}>
              <Item
                key={formConfigIndex}
                form={form}
                parentDisabled={disabled}
                item={formConfigItem}
                prefixIdentification={[identification, index]}
                tiledFormDataRef={tiledFormDataRef}
              />
            </Col>
          ))}
        </Row>
      </div>)}
    </div>

  </div>);
};

/**
 * 分组项
 * @description
 * 参考 saas-manage/src/views/resmng/pages/detail/components/DynamicSubForm/components/SubFormDynamicComponent.tsx
 * @demo
 */
const Item:FC<{
  form,
  /** 父级禁用，子级也应该禁用 */
  parentDisabled?: boolean;
  item,
  /** 前缀标识，如 ['subform01', 0] */
  prefixIdentification: any[],
  tiledFormDataRef,
}> = ({
  form,
  parentDisabled,
  item,
  prefixIdentification,
  tiledFormDataRef,
}) => {

  /* state */
  const disabled = parentDisabled || item.disabled;
  const required = item.required === 1; // saas 后台是这样配置的，见 saas-manage/src/views/resmng/pages/detail/components/DynamicSubForm/components/SubFormDynamicComponent.tsx

  /* hooks */

  /* methods */

  const propertyItem = {
    ...item,
    identification: [...(Array.isArray(prefixIdentification) ? prefixIdentification : []), item.identification]
  };

  return (<div>
    {
      // 单选组件
      propertyItem.controlType === ControlType.SINGLE_RADIO.value &&
      (Array.isArray(propertyItem.propertyConfigOptionVOList) &&
      propertyItem.propertyConfigOptionVOList.length > 0 &&
      propertyItem.propertyConfigOptionVOList.length < 3
        ? <Radio form={form} propertyItem={propertyItem} disabled={disabled} required={required} />
        : <DropdownSelect form={form} propertyItem={propertyItem} disabled={disabled} required={required} />)
    }
    {
      // 多选组件
      propertyItem.controlType === ControlType.CHECK_BOX.value &&
       <Checkbox form={form} propertyItem={propertyItem} disabled={disabled} required={required} />
    }
    {
      // 单行输入框
      propertyItem.controlType === ControlType.INPUT.value &&
      <Input propertyItem={propertyItem} disabled={disabled} required={required} />
    }
    {propertyItem.controlType === ControlType.TREE_SELECT.value &&
      <TreeSelect form={form} propertyItem={propertyItem} disabled={disabled} required={required} />}
    {
      // 多行文本框
      propertyItem.controlType === ControlType.TEXT_AREA.value &&
      <TextareaInput form={form} propertyItem={propertyItem} disabled={disabled} required={required} tiledFormDataRef={tiledFormDataRef} />
    }
    {
      // 数字输入框组件
      propertyItem.controlType === ControlType.INPUT_NUMBER.value &&
       <NumberInput form={form} propertyItem={propertyItem} disabled={disabled} required={required} tiledFormDataRef={tiledFormDataRef} />
    }
    {
      // 日期组件
      propertyItem.controlType === ControlType.TIME.value &&
      <Time propertyItem={propertyItem} disabled={disabled} required={required} />
    }
  </div>);
};

export default Component;
